import * as React from 'react';
import { Bookmark, Plus, Trash2, X, Pencil } from 'lucide-react';
import { Button, Input, Card, CardHeader, CardTitle, CardContent } from '@/components/ui';
import { api } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';

interface SavedView {
  id: string;
  name: string;
  filters: Record<string, string>;
  createdAt: string;
}

interface SavedViewsProps {
  currentFilters: Record<string, string>;
  onApply: (filters: Record<string, string>) => void;
}

export function SavedViews({ currentFilters, onApply }: SavedViewsProps) {
  const { t } = useI18n();
  const [views, setViews] = React.useState<SavedView[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [showCreate, setShowCreate] = React.useState(false);
  const [name, setName] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  // TICKET-14: inline rename state — which view (if any) is currently being
  // renamed, and the in-progress text for it.
  const [renamingId, setRenamingId] = React.useState<string | null>(null);
  const [renameValue, setRenameValue] = React.useState('');

  React.useEffect(() => {
    api
      .get<{ data: SavedView[] }>('/tickets/views')
      .then((res) => setViews(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleCreate() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const res = await api.post<{ data: SavedView }>('/tickets/views', {
        name: name.trim(),
        filters: currentFilters,
      });
      setViews((prev) => [...prev, res.data]);
      setName('');
      setShowCreate(false);
    } catch (err: unknown) {
      console.error('Failed to save view:', err);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    try {
      await api.delete(`/tickets/views/${id}`);
      setViews((prev) => prev.filter((v) => v.id !== id));
    } catch (err: unknown) {
      console.error('Failed to delete view:', err);
    }
  }

  function startRename(view: SavedView) {
    setRenamingId(view.id);
    setRenameValue(view.name);
  }

  function cancelRename() {
    setRenamingId(null);
    setRenameValue('');
  }

  async function commitRename(id: string) {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      cancelRename();
      return;
    }
    try {
      const res = await api.patch<{ data: SavedView }>(`/tickets/views/${id}`, { name: trimmed });
      setViews((prev) => prev.map((v) => (v.id === id ? res.data : v)));
    } catch (err: unknown) {
      console.error('Failed to rename view:', err);
    } finally {
      cancelRename();
    }
  }

  const hasFilters = Object.values(currentFilters).some(Boolean);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">{t('savedViews.title')}</CardTitle>
          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowCreate(true)}
              className="h-6 px-2"
              aria-label={t('savedViews.saveCurrent')}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {showCreate && (
          <div className="flex items-center gap-2">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('savedViews.namePlaceholder')}
              className="h-7 text-xs"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
              }}
              aria-label={t('savedViews.nameAria')}
            />
            <Button
              size="sm"
              className="h-7 px-2 text-xs"
              disabled={saving || !name.trim()}
              onClick={handleCreate}
            >
              {t('common.save')}
            </Button>
            <button
              type="button"
              onClick={() => {
                setShowCreate(false);
                setName('');
              }}
              className="text-ink-subtle hover:text-ink-muted"
              aria-label={t('common.cancel')}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {loading ? (
          <p className="py-2 text-center text-xs text-ink-subtle">{t('common.loading')}</p>
        ) : views.length === 0 ? (
          <p className="py-2 text-center text-xs text-ink-subtle">
            {t('savedViews.empty')}
          </p>
        ) : (
          <ul className="space-y-1" aria-label={t('savedViews.listAria')}>
            {views.map((view) =>
              renamingId === view.id ? (
                <li key={view.id} className="flex items-center gap-2 px-2 py-1">
                  <Input
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    className="h-7 flex-1 text-xs"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void commitRename(view.id);
                      if (e.key === 'Escape') cancelRename();
                    }}
                    onBlur={() => void commitRename(view.id)}
                    aria-label={t('savedViews.rename', { name: view.name })}
                  />
                </li>
              ) : (
                <li key={view.id} className="group flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onApply(view.filters)}
                    className="flex flex-1 items-center gap-1.5 rounded px-2 py-1.5 text-left text-xs text-ink-muted hover:bg-surface-hover"
                  >
                    <Bookmark className="h-3 w-3 shrink-0 text-brand-500" aria-hidden="true" />
                    <span className="truncate">{view.name}</span>
                  </button>
                  {/* TICKET-14: rename support. */}
                  <button
                    type="button"
                    onClick={() => startRename(view)}
                    className="invisible text-ink-subtle hover:text-brand-500 group-hover:visible"
                    aria-label={t('savedViews.rename', { name: view.name })}
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(view.id)}
                    className="invisible text-ink-subtle hover:text-danger-500 group-hover:visible"
                    aria-label={t('savedViews.delete', { name: view.name })}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </li>
              ),
            )}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
